import React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Plus,
  Edit,
  Trash2,
  Target,
  Eye,
  Clock,
  Users,
  TrendingUp,
  Zap,
  DollarSign,
  ArrowRight,
} from 'lucide-react'
import {
  useAdminInitiatives,
  useDeleteAdminInitiative,
} from '@/features/project-initiatives/hooks'
import { ImportExportButtons } from '@/components/ui/import-export-buttons'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'
import { DataPagination } from '@/components/ui/data-pagination'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'

interface ProjectInitiative {
  id: string
  title: string
  description: string
  short_description: string
  from_maturity_level: number
  to_maturity_level: number
  domain: string
  priority: string
  impact_level: string
  effort_level: string
  duration_weeks: number
  team_size_min: number
  team_size_max: number
  budget_range_min?: number
  budget_range_max?: number
  required_technologies: string[]
  required_skills: string[]
  recommended_tools: string[]
  success_metrics: string[]
  kpis: string[]
  prerequisites: string[]
  dependencies: string[]
  phases: any[]
  risk_level: string
  risk_factors: string[]
  mitigation_strategies: string[]
  business_value: string
  roi_estimate: string
  is_active: boolean
  created_at: string
  updated_at: string
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200',
}

const IMPACT_ICONS: Record<string, string> = {
  high: '🔴',
  medium: '🟡',
  low: '🟢',
  transformational: '🔴',
  significant: '🟠',
  moderate: '🟡',
  minimal: '🟢',
}

const cardVariants = {
  hidden: (i: number) => ({ opacity: 0, y: 20, transition: { delay: i * 0.05 } }),
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.3 } }),
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
}

const ProjectInitiativesManagement: React.FC = () => {
  const listState = useListState({ defaultPerPage: 6, defaultSortBy: 'priority', defaultSortOrder: 'asc' })

  const navigate = useNavigate()

  const { data: initiativesData, isLoading } = useAdminInitiatives(listState.queryParams)

  const deleteMutation = useDeleteAdminInitiative()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Target className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p>Loading project initiatives...</p>
        </div>
      </div>
    )
  }

  const initiativesRaw = initiativesData?.data ?? initiativesData
  const initiatives = Array.isArray(initiativesRaw)
    ? initiativesRaw
    : (initiativesRaw?.items || initiativesRaw?.initiatives || [])
  const pagination = Array.isArray(initiativesRaw) ? null : (
    initiativesRaw?.pages != null
      ? { total: initiativesRaw.total, page: initiativesRaw.page, per_page: initiativesRaw.per_page, pages: initiativesRaw.pages }
      : initiativesRaw?.pagination ?? null
  )

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Project Initiatives Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage project initiatives and their attributes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AuthorizationGate actions={['export_initiatives', 'import_initiatives']} mode="any">
            <ImportExportButtons
              entityType="project_initiatives"
              entityLabel="Project Initiatives"
            />
          </AuthorizationGate>
          <AuthorizationGate actions={['create_initiative']}>
            <Button onClick={() => navigate('/settings/recommendations/initiatives/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Add Initiative
            </Button>
          </AuthorizationGate>
        </div>
      </div>

      {/* Toolbar */}
      <DataToolbar
        listState={listState}
        searchPlaceholder="Search initiatives..."
        sortOptions={[
          { label: 'Priority', value: 'priority' },
          { label: 'Title', value: 'title' },
          { label: 'Domain', value: 'domain' },
          { label: 'Created', value: 'created_at' },
        ]}
        totalItems={pagination?.total}
        perPageOptions={[6, 9, 12, 24]}
      />

      {/* Initiative Cards Grid */}
      {initiatives.length === 0 ? (
        <motion.div
          className="text-center py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Target className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-muted-foreground">No project initiatives found.</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Click "Add Initiative" to create one.</p>
        </motion.div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={listState.page}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            {initiatives.map((initiative: ProjectInitiative, index: number) => (
              <motion.div
                key={initiative.id}
                custom={index}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
              >
                <Card className="h-full flex flex-col hover:shadow-md transition-shadow duration-200 border border-gray-200">
                  <CardContent className="p-5 flex flex-col h-full">
                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0 mr-2">
                        <h4 className="font-semibold text-sm leading-tight line-clamp-2">{initiative.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{initiative.short_description}</p>
                      </div>
                      <Badge
                        variant={initiative.is_active ? 'default' : 'secondary'}
                        className="text-[10px] shrink-0"
                      >
                        {initiative.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    {/* Badges Row */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <Badge variant="outline" className="text-[10px] capitalize">{initiative.domain}</Badge>
                      <Badge className={`text-[10px] capitalize border ${PRIORITY_COLORS[initiative.priority] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                        {initiative.priority}
                      </Badge>
                      <Badge variant="secondary" className="text-[10px]">
                        L{initiative.from_maturity_level} <ArrowRight className="h-2.5 w-2.5 mx-0.5 inline" /> L{initiative.to_maturity_level}
                      </Badge>
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-muted-foreground mb-4 flex-1">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3 shrink-0" />
                        <span>{initiative.duration_weeks} weeks</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3 w-3 shrink-0" />
                        <span>{initiative.team_size_min}–{initiative.team_size_max} people</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <TrendingUp className="h-3 w-3 shrink-0" />
                        <span className="capitalize">{IMPACT_ICONS[initiative.impact_level] || ''} {initiative.impact_level}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Zap className="h-3 w-3 shrink-0" />
                        <span className="capitalize">{initiative.effort_level} effort</span>
                      </div>
                      {initiative.budget_range_min && initiative.budget_range_max && (
                        <div className="flex items-center gap-1.5 col-span-2">
                          <DollarSign className="h-3 w-3 shrink-0" />
                          <span>${initiative.budget_range_min}k – ${initiative.budget_range_max}k</span>
                        </div>
                      )}
                    </div>

                    {/* Card Actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => navigate(`/settings/recommendations/initiatives/${initiative.id}`)}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View Details
                      </Button>
                      <div className="flex gap-1">
                        <AuthorizationGate actions={['update_initiative']}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/settings/recommendations/initiatives/${initiative.id}?edit=true`)}
                            className="h-7 w-7 p-0"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        </AuthorizationGate>
                        <AuthorizationGate actions={['delete_initiative']}>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Project Initiative</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{initiative.title}"?
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteMutation.mutate(initiative.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </AuthorizationGate>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      )}

      {/* Pagination */}
      {pagination && (
        <DataPagination
          page={listState.page}
          totalPages={pagination.pages ?? 1}
          onPageChange={listState.setPage}
          totalItems={pagination.total}
          perPage={listState.perPage}
        />
      )}
    </motion.div>
  )
}

export default ProjectInitiativesManagement
